import { useEffect, useRef, useState } from 'react';
import { Listbox } from '@headlessui/react';
import {
	CheckIcon,
	ChevronUpDownIcon,
} from '@heroicons/react/20/solid';

import { createProject } from '../api/projects';
import { getTeam } from '../api/company';
import { getModuleCategoriesByModuleKey } from '../api/modules';

import { StackableModal, DateTimePicker } from '../components/nextgen';
import { classNames, getUserFullName } from '../components/helpers/functions';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'createProject';

const PRIORITIES = [
	{ id: 'low', name: 'Low' },
	{ id: 'medium', name: 'Medium' },
	{ id: 'high', name: 'High' },
	{ id: 'critical', name: 'Critical' },
];

const CreateProjectModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const nameInputRef = useRef(null);

	const [form, setForm] = useState({
		name: '',
		description: '',
		categoryId: null,
		managerId: null,
		startDate: '',
		targetEndDate: '',
		priority: 'medium',
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [categories, setCategories] = useState([]);

	// Data fetching
	const { value: team } = useAsync(getTeam);

	// Load categories (optional - may not have a projects module yet)
	useEffect(() => {
		getModuleCategoriesByModuleKey({ key: 'default:project' })
			.then((res) => {
				setCategories(res?.rows || res || []);
			})
			.catch(() => {
				// No projects module configured yet - categories will be empty
				setCategories([]);
			});
	}, []);

	// Reset form when modal opens
	useEffect(() => {
		if (open) {
			setForm({
				name: '',
				description: '',
				categoryId: null,
				managerId: null,
				startDate: '',
				targetEndDate: '',
				priority: 'medium',
			});
			setTimeout(() => nameInputRef.current?.focus(), 100);
		}
	}, [open]);

	const handleChange = (field) => (e) => {
		const value = e?.target?.value ?? e;
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmit = async () => {
		if (!form.name.trim()) return;

		setIsSubmitting(true);
		try {
			await createProject(form);
			open?.onSuccess?.();
			setClose();
		} catch (error) {
			console.error('Failed to create project:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const selectedCategory = categories.find((c) => c.id === form.categoryId);
	const selectedManager = team?.rows?.find((u) => u.id === form.managerId);
	const selectedPriority = PRIORITIES.find((p) => p.id === form.priority);

	return (
		<StackableModal
			isOpen={!!open}
			onClose={setClose}
			title="Create Project"
			size="lg"
			footer={
				<div className="flex justify-end gap-3">
					<button
						type="button"
						onClick={setClose}
						className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleSubmit}
						disabled={isSubmitting || !form.name.trim()}
						className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
					>
						{isSubmitting ? 'Creating...' : 'Create Project'}
					</button>
				</div>
			}
		>
			<div className="space-y-6">
				{/* Name */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Project Name *
					</label>
					<input
						ref={nameInputRef}
						type="text"
						value={form.name}
						onChange={handleChange('name')}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
						placeholder="Enter project name"
					/>
				</div>

				{/* Description */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Description
					</label>
					<textarea
						value={form.description}
						onChange={handleChange('description')}
						rows={4}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
						placeholder="Enter project description"
					/>
				</div>

				<div className="grid grid-cols-2 gap-4">
					{/* Category */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Category
						</label>
						<Listbox
							value={form.categoryId}
							onChange={handleChange('categoryId')}
						>
							<div className="relative">
								<Listbox.Button className="relative w-full cursor-default rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-blue-500">
									<span className="block truncate">
										{selectedCategory?.name || 'Select category'}
									</span>
									<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
										<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
									</span>
								</Listbox.Button>
								<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg border border-gray-200">
									{categories.map((category) => (
										<Listbox.Option
											key={category.id}
											value={category.id}
											className={({ active }) =>
												classNames(
													'relative cursor-default select-none py-2 pl-10 pr-4',
													active ? 'bg-blue-100 text-blue-900' : 'text-gray-900',
												)
											}
										>
											{({ selected }) => (
												<>
													<span className={classNames('block truncate', selected && 'font-medium')}>
														{category.name}
													</span>
													{selected && (
														<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
															<CheckIcon className="h-5 w-5" />
														</span>
													)}
												</>
											)}
										</Listbox.Option>
									))}
								</Listbox.Options>
							</div>
						</Listbox>
					</div>

					{/* Priority */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Priority
						</label>
						<Listbox value={form.priority} onChange={handleChange('priority')}>
							<div className="relative">
								<Listbox.Button className="relative w-full cursor-default rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-blue-500">
									<span className="block truncate">
										{selectedPriority?.name || 'Select priority'}
									</span>
									<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
										<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
									</span>
								</Listbox.Button>
								<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg border border-gray-200">
									{PRIORITIES.map((priority) => (
										<Listbox.Option
											key={priority.id}
											value={priority.id}
											className={({ active }) =>
												classNames(
													'relative cursor-default select-none py-2 pl-10 pr-4',
													active ? 'bg-blue-100 text-blue-900' : 'text-gray-900',
												)
											}
										>
											{({ selected }) => (
												<>
													<span className={classNames('block truncate', selected && 'font-medium')}>
														{priority.name}
													</span>
													{selected && (
														<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
															<CheckIcon className="h-5 w-5" />
														</span>
													)}
												</>
											)}
										</Listbox.Option>
									))}
								</Listbox.Options>
							</div>
						</Listbox>
					</div>
				</div>

				{/* Manager */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Project Manager
					</label>
					<Listbox value={form.managerId} onChange={handleChange('managerId')}>
						<div className="relative">
							<Listbox.Button className="relative w-full cursor-default rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-blue-500">
								<span className="block truncate">
									{selectedManager ? getUserFullName(selectedManager) : 'Select manager'}
								</span>
								<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
									<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
								</span>
							</Listbox.Button>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg border border-gray-200">
								{team?.rows?.map((user) => (
									<Listbox.Option
										key={user.id}
										value={user.id}
										className={({ active }) =>
											classNames(
												'relative cursor-default select-none py-2 pl-10 pr-4',
												active ? 'bg-blue-100 text-blue-900' : 'text-gray-900',
											)
										}
									>
										{({ selected }) => (
											<>
												<span className={classNames('block truncate', selected && 'font-medium')}>
													{getUserFullName(user)}
												</span>
												{selected && (
													<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
														<CheckIcon className="h-5 w-5" />
													</span>
												)}
											</>
										)}
									</Listbox.Option>
								))}
							</Listbox.Options>
						</div>
					</Listbox>
				</div>

				<div className="grid grid-cols-2 gap-4">
					{/* Start Date */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Start Date
						</label>
						<input
							type="date"
							value={form.startDate}
							onChange={handleChange('startDate')}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>

					{/* Target End Date */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Target End Date
						</label>
						<input
							type="date"
							value={form.targetEndDate}
							onChange={handleChange('targetEndDate')}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>
				</div>
			</div>
		</StackableModal>
	);
};

export default CreateProjectModal;
